// =============================================================
//  Kit UI — Composants de base du dashboard admin
//  (Card, StatCard, Badge, Button, IconButton, Input, Select,
//  EmptyState, Table, Skeleton, SearchBar, PageHeader, etc.)
// =============================================================

import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  StyleSheet,
  ScrollView,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colors, radius, spacing, shadow } from './theme';

// ---------- Card ----------
export const Card = ({ children, style, padding = spacing.lg, noPadding }) => (
  <View
    style={[
      kit.card,
      { padding: noPadding ? 0 : padding },
      style,
    ]}
  >
    {children}
  </View>
);

// ---------- PageHeader ----------
export const PageHeader = ({ title, subtitle, actions }) => (
  <View style={kit.pageHeader}>
    <View style={{ flex: 1, minWidth: 0 }}>
      <Text style={kit.pageTitle} numberOfLines={1}>{title}</Text>
      {!!subtitle && (
        <Text style={kit.pageSubtitle} numberOfLines={1}>{subtitle}</Text>
      )}
    </View>
    {!!actions && <View style={kit.pageActions}>{actions}</View>}
  </View>
);

// ---------- StatCard ----------
export const StatCard = ({ label, value, icon, accent = colors.primary, delta, loading }) => (
  <Card style={{ minWidth: 200, flex: 1 }}>
    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={kit.statLabel}>{label}</Text>
        {loading ? (
          <Skeleton width={80} height={28} style={{ marginTop: 4 }} />
        ) : (
          <Text style={kit.statValue} numberOfLines={1}>{value}</Text>
        )}
        {delta != null && !loading && (
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 6 }}>
            <MaterialCommunityIcons
              name={delta >= 0 ? 'trending-up' : 'trending-down'}
              size={14}
              color={delta >= 0 ? colors.success : colors.danger}
            />
            <Text
              style={{
                fontSize: 12,
                fontWeight: '600',
                marginLeft: 4,
                color: delta >= 0 ? colors.success : colors.danger,
              }}
            >
              {delta >= 0 ? '+' : ''}{delta}%
            </Text>
            <Text style={{ fontSize: 12, color: colors.textMuted, marginLeft: 4 }}>
              vs mois dernier
            </Text>
          </View>
        )}
      </View>
      {!!icon && (
        <View
          style={{
            width: 48,
            height: 48,
            borderRadius: radius.lg,
            backgroundColor: accent + '18',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MaterialCommunityIcons name={icon} size={26} color={accent} />
        </View>
      )}
    </View>
  </Card>
);

// ---------- Badge ----------
const BADGE_VARIANTS = {
  primary: { bg: colors.primarySoft, fg: colors.primary },
  success: { bg: colors.successSoft, fg: colors.success },
  warning: { bg: colors.warningSoft, fg: colors.warning },
  danger: { bg: colors.dangerSoft, fg: colors.danger },
  info: { bg: colors.infoSoft, fg: colors.info },
  neutral: { bg: colors.surfaceAlt, fg: colors.textSecondary },
};
export const Badge = ({ children, variant = 'neutral', icon }) => {
  const v = BADGE_VARIANTS[variant] || BADGE_VARIANTS.neutral;
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        backgroundColor: v.bg,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: radius.pill,
        gap: 4,
      }}
    >
      {!!icon && <MaterialCommunityIcons name={icon} size={12} color={v.fg} />}
      <Text style={{ color: v.fg, fontSize: 11, fontWeight: '700' }}>{children}</Text>
    </View>
  );
};

// ---------- Button ----------
const BTN_VARIANTS = {
  primary: { bg: colors.primary, fg: '#fff', border: 'transparent' },
  secondary: { bg: colors.surface, fg: colors.text, border: colors.border },
  danger: { bg: colors.danger, fg: '#fff', border: 'transparent' },
  ghost: { bg: 'transparent', fg: colors.text, border: 'transparent' },
};
export const Button = ({
  children,
  onPress,
  variant = 'primary',
  size = 'md',
  icon,
  loading,
  disabled,
  style,
  fullWidth,
}) => {
  const v = BTN_VARIANTS[variant] || BTN_VARIANTS.primary;
  const heights = { sm: 32, md: 40, lg: 48 };
  const paddings = { sm: 10, md: 14, lg: 18 };
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled || loading}
      activeOpacity={0.85}
      style={[
        {
          height: heights[size],
          paddingHorizontal: paddings[size],
          borderRadius: radius.md,
          backgroundColor: v.bg,
          borderWidth: 1,
          borderColor: v.border,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: disabled ? 0.5 : 1,
          alignSelf: fullWidth ? 'stretch' : 'flex-start',
        },
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator size="small" color={v.fg} />
      ) : (
        <>
          {!!icon && (
            <MaterialCommunityIcons
              name={icon}
              size={size === 'sm' ? 14 : 16}
              color={v.fg}
              style={{ marginRight: children ? 6 : 0 }}
            />
          )}
          {!!children && (
            <Text style={{ color: v.fg, fontWeight: '600', fontSize: size === 'sm' ? 13 : 14 }}>
              {children}
            </Text>
          )}
        </>
      )}
    </TouchableOpacity>
  );
};

// ---------- IconButton ----------
export const IconButton = ({ icon, onPress, color = colors.text, size = 36, iconSize = 18, badge }) => (
  <TouchableOpacity
    onPress={onPress}
    activeOpacity={0.7}
    style={{
      width: size,
      height: size,
      borderRadius: size / 2,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.surfaceAlt,
    }}
  >
    <MaterialCommunityIcons name={icon} size={iconSize} color={color} />
    {badge != null && badge > 0 && (
      <View
        style={{
          position: 'absolute',
          top: -2,
          right: -2,
          minWidth: 16,
          height: 16,
          borderRadius: 8,
          backgroundColor: colors.danger,
          paddingHorizontal: 4,
          alignItems: 'center',
          justifyContent: 'center',
          borderWidth: 2,
          borderColor: colors.surface,
        }}
      >
        <Text style={{ color: '#fff', fontSize: 9, fontWeight: '700' }}>
          {badge > 99 ? '99+' : badge}
        </Text>
      </View>
    )}
  </TouchableOpacity>
);

// ---------- Input ----------
export const Input = ({
  value,
  onChangeText,
  placeholder,
  icon,
  type = 'text',
  multiline,
  numberOfLines,
  rightIcon,
  onRightIconPress,
  ...rest
}) => (
  <View
    style={{
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.md,
      paddingHorizontal: 12,
      minHeight: multiline ? 80 : 40,
    }}
  >
    {!!icon && (
      <MaterialCommunityIcons
        name={icon}
        size={16}
        color={colors.textMuted}
        style={{ marginRight: 8 }}
      />
    )}
    <TextInput
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={colors.textMuted}
      style={{
        flex: 1,
        color: colors.text,
        fontSize: 14,
        outlineStyle: 'none',
        paddingVertical: multiline ? 10 : 0,
      }}
      secureTextEntry={type === 'password'}
      keyboardType={type === 'number' ? 'numeric' : type === 'email' ? 'email-address' : 'default'}
      multiline={multiline}
      numberOfLines={numberOfLines}
      {...rest}
    />
    {!!rightIcon && (
      <TouchableOpacity onPress={onRightIconPress} style={{ marginLeft: 8 }}>
        <MaterialCommunityIcons name={rightIcon} size={16} color={colors.textMuted} />
      </TouchableOpacity>
    )}
  </View>
);

// ---------- SearchBar ----------
export const SearchBar = ({ value, onChangeText, placeholder = 'Rechercher...' }) => (
  <Input icon="magnify" value={value} onChangeText={onChangeText} placeholder={placeholder} />
);

// ---------- Select (simple dropdown via boutons row) ----------
export const SegmentedSelect = ({ options, value, onChange }) => (
  <ScrollView
    horizontal
    showsHorizontalScrollIndicator={false}
    contentContainerStyle={{ gap: 6 }}
  >
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <TouchableOpacity
          key={opt.value}
          onPress={() => onChange(opt.value)}
          style={{
            paddingHorizontal: 14,
            paddingVertical: 8,
            borderRadius: radius.pill,
            backgroundColor: active ? colors.primary : colors.surfaceAlt,
            borderWidth: 1,
            borderColor: active ? colors.primary : colors.border,
          }}
        >
          <Text
            style={{
              color: active ? '#fff' : colors.textSecondary,
              fontSize: 13,
              fontWeight: active ? '700' : '500',
            }}
          >
            {opt.label}
          </Text>
        </TouchableOpacity>
      );
    })}
  </ScrollView>
);

// ---------- EmptyState ----------
export const EmptyState = ({ icon = 'inbox-outline', title, subtitle, action }) => (
  <View style={{ alignItems: 'center', paddingVertical: 60, paddingHorizontal: 20 }}>
    <View
      style={{
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: colors.surfaceAlt,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
      }}
    >
      <MaterialCommunityIcons name={icon} size={32} color={colors.textMuted} />
    </View>
    <Text style={{ fontSize: 16, fontWeight: '700', color: colors.text }}>{title}</Text>
    {!!subtitle && (
      <Text
        style={{
          fontSize: 13,
          color: colors.textSecondary,
          marginTop: 6,
          textAlign: 'center',
          maxWidth: 360,
        }}
      >
        {subtitle}
      </Text>
    )}
    {!!action && <View style={{ marginTop: 16 }}>{action}</View>}
  </View>
);

// ---------- Skeleton ----------
export const Skeleton = ({ width = '100%', height = 14, radius: r = 6, style }) => (
  <View
    style={[
      { width, height, borderRadius: r, backgroundColor: colors.surfaceAlt },
      style,
    ]}
  />
);

// ---------- Loading center ----------
export const Loading = ({ label }) => (
  <View style={{ alignItems: 'center', justifyContent: 'center', padding: 40 }}>
    <ActivityIndicator size="large" color={colors.primary} />
    {!!label && (
      <Text style={{ marginTop: 12, color: colors.textSecondary, fontSize: 13 }}>{label}</Text>
    )}
  </View>
);

// ---------- Divider ----------
export const Divider = ({ style }) => (
  <View style={[{ height: 1, backgroundColor: colors.border, marginVertical: spacing.md }, style]} />
);

// ---------- Avatar ----------
export const Avatar = ({ src, name = '?', size = 36 }) => {
  const initials = (name || '?').slice(0, 2).toUpperCase();
  if (src) {
    return (
      <View
        style={{
          width: size,
          height: size,
          borderRadius: size / 2,
          overflow: 'hidden',
          backgroundColor: colors.surfaceAlt,
        }}
      >
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img
          src={src}
          style={{ width: size, height: size, objectFit: 'cover' }}
        />
      </View>
    );
  }
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: colors.primarySoft,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Text style={{ color: colors.primary, fontWeight: '700', fontSize: size * 0.4 }}>
        {initials}
      </Text>
    </View>
  );
};

// ---------- DataRow (ligne d'une table) ----------
export const DataRow = ({ children, onPress, last }) => {
  const Component = onPress ? TouchableOpacity : View;
  return (
    <Component
      onPress={onPress}
      activeOpacity={0.7}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
        paddingHorizontal: 16,
        borderBottomWidth: last ? 0 : 1,
        borderBottomColor: colors.border,
        gap: 12,
      }}
    >
      {children}
    </Component>
  );
};

// ---------- styles ----------
const kit = StyleSheet.create({
  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    ...shadow.sm,
  },
  pageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.lg,
    marginBottom: spacing.xl,
    flexWrap: 'wrap',
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.text,
    letterSpacing: -0.3,
  },
  pageSubtitle: {
    fontSize: 13,
    color: colors.textSecondary,
    marginTop: 4,
  },
  pageActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  statLabel: {
    fontSize: 12,
    color: colors.textSecondary,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  statValue: {
    fontSize: 26,
    fontWeight: '700',
    color: colors.text,
    marginTop: 6,
    letterSpacing: -0.4,
  },
});
